import React from 'react'
import { Controller, useForm, useFormContext } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import InputMask from 'react-input-mask'
import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable';
import _get from 'lodash/get'

import { dropDecimals } from '../helpers/common'

const getError = (errors, name) => {
  return (errors) ? _get(errors, name) : undefined
}

function FormError({ errorClass, ...props }) {
  return (<span className={'form-message error ' + (errorClass || '')}>
    {props.msg}
  </span>)
}

export function HRadio(props) {
  const formContext = useFormContext()
  let {
    id,
    errors,
    pillboxStyle,
    checkboxStyle,
    unregister,
    register,
    name,
    labels,
    watch,
    getValues,
    setValue,
    control,
    reset,
    errorClass,
    defaultCheckedLabel,
    ...rest
  } = props

  if (!!formContext) {
    register = formContext.register
    errors = formContext.errors
    control = formContext.control
  }
  const err = getError(errors, name)
  let className;
  if (pillboxStyle) className = 'radio-pillbox';
  if (checkboxStyle) className = 'checkbox';

  return (
    <React.Fragment>
      {labels && labels.map(label => (
        <div className={pillboxStyle || checkboxStyle ? className : 'radio'} key={label}>  
          <label htmlFor={id} tabIndex={0}>
            <input
              ref={register ? register() : register}
              id={id} type='radio'
              name={name}
              value={label} {...rest}
              defaultChecked = {label === defaultCheckedLabel}
            />
            <span>{label}</span>
          </label>
        </div>
      ))}
      {err && <FormError errorClass={errorClass} msg={err.message} />}
    </React.Fragment>
  )
}

export function HCheckbox(props) {
  const formContext = useFormContext()
  let {
    errors,
    register,
    unregister,
    name,
    watch,
    getValues,
    setValue,
    control,
    reset,
    defaultValue,
    decimals,
    onChange,
    maxLength,
    hidden,
    label,
    className,
    ...rest
  } = props

  if (!!formContext) {
    register = formContext.register
    errors = formContext.errors
    control = formContext.control
  }
  const err = getError(errors, name)

  return (
    <React.Fragment>
      <div className='checkbox' style={{ display: hidden ? 'none' : 'block' }}>
        <label>
          <input
            name={name}
            type='checkbox'
            ref={register ? register() : register}
            defaultChecked={defaultValue}
            onClick={onChange}
            className={className + (err ? ' invalid' : '')}
            {...rest}
          />
          <span className='checkbox-label'>{label}</span>
        </label>
      </div>
      {err && <FormError msg={err.message} />}
    </React.Fragment>
  )
}

export function HCheckboxRadio({
  register,
  control,
  errors,
  name,
  label,
  title,
  className = '',
  hidden = false,
  defaultValue = false,
  onChange,
  ...rest
}) {
  const formContext = useFormContext()
  if (!!formContext) {
    register = formContext.register
    errors = formContext.errors
    control = formContext.control
  }

  const errorMessage = errors[name]?.message;

  return (
    <>
      <div className="checkbox-radio">
        <label className="switch">
          <input
            type="checkbox"
            name={name}
            id={name}
            ref={register}
            defaultChecked={defaultValue}
            onChange={onChange}
            className={`${className} ${errorMessage ? 'invalid' : ''}`}
            {...rest}
          />
        <span className="slider round"></span>
        </label>
        <span className='title-payment'>{title}</span>
      </div>
      {errorMessage && <FormError msg={errorMessage} />}
      </>
  );
}

export const HInput = (props) => {
  const formContext = useFormContext()
  let {
    errors,
    register,
    unregister,
    name,
    watch,
    getValues,
    setValue,
    control,
    reset,
    defaultValue,
    decimals,
    onChange,
    maxLength,
    minus,
    ...rest
  } = props

  // hack: copy context if available
  if (!!formContext) {
    register = formContext.register
    errors = formContext.errors
  }

  const err = getError(errors, name)
  rest.className += err ? ' invalid' : ''

  const handleOnChange = (e) => {
    if (decimals !== undefined) {
      e.target.value = dropDecimals(e.target.value, decimals, minus)
    }

    if (onChange) {
      onChange(e)
    }
  }

  return (
    <React.Fragment>
      <input
        name={name}
        ref={register ? register() : register}
        defaultValue={defaultValue}
        onChange={handleOnChange}
        maxLength={maxLength}
        {...rest}
      />
      {!rest.hidden && err && <FormError msg={err.message} />}
    </React.Fragment>
  )
}

export function HMaskedInput({ errors, register, unregister, name, watch, getValues,setValue, mask, control, defaultValue, reset = '', ...rest }) {
  let err = getError(errors, name)
  if (err) {
    rest.className += ' invalid'
  }

  return (
    <React.Fragment>
      <Controller as={InputMask}
        control={control}
        mask={mask}
        name={name}
        alwaysShowMask={true}
        defaultValue={defaultValue}
        {...rest} />
      {err && <FormError msg={err.message} />}
    </React.Fragment>
  )
}

export function HDatePicker(props) {
  const formContext = useFormContext()
  let { errors, register, unregister, name, watch, getValues,setValue, control, reset, readOnly = false, ...rest } = props
  // hack: copy context if available
  if (!!formContext) {
    register = formContext.register
    errors = formContext.errors
    control = formContext.control
  }
  let err = getError(errors, name)
  if (err) {
    rest.className += ' invalid'
  }

  return (
    <React.Fragment>
      <input name={name}
             ref={register ? register() : register}
             {...rest}
             type={rest?.type ? rest.type : 'date'}
             max={rest?.max ? rest.max : '2100-01-01'}
             readOnly={readOnly}
      />
      {err && <FormError msg={err.message} />}
    </React.Fragment>
  )
}

export function HTextarea(props) {
  const formContext = useFormContext()
  let { errors, register, unregister, name, watch, getValues,setValue, control, reset, ...rest } = props
  // hack: copy context if available
  if (!!formContext) {
    register = formContext.register
    errors = formContext.errors
    control = formContext.control
  }
  let err = getError(errors, name)
  if (err) {
    rest.className += ' invalid'
  }
  return (
    <React.Fragment>
      <textarea name={name} ref={register ? register() : register} {...rest} />
      {err && <FormError msg={err.message} />}
    </React.Fragment>
  )
}

export function HSelect({ errors, register, unregister, options, name, watch, getValues,setValue, control, reset, ...rest }) {
  let err = getError(errors, name)
  if (err) {
    rest.className += ' invalid'
  }
  return (
    <React.Fragment>
      <select name={name} ref={register ? register() : register} {...rest}>
        {options.map(value => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>
      {err && <FormError msg={err.message} />}
    </React.Fragment>
  )
}

export const DSelect = (props) => {
  const formContext = useFormContext()
  let {
    register, unregister, errors, watch, name, getValues,setValue, control, reset,
    defaultValue,
    handleOptions,
    isSearchable,
    isMulti,
    options,
    placeholder,
    handleOnChange,
    handleChangeContent,
    isLoading,
    className,
    cmpnts,
    maxMenuHeight,
    isCreatable,
    ...rest
  } = props

  // hack: use context if available
  if (!!formContext) {
    register = formContext.register
    errors = formContext.errors
    control = formContext.control
  }

  const err = getError(errors, name)
  let message
  let valueName = className ? `basic-multi-select ${className}` : 'basic-multi-select'
  if (err) {
    message = err?.label ? err.label.message : err.message
    message = message || err?.value?.message
    valueName = valueName.split(' ')
      .map((i, ind) => ind ? i : `${i}-invalid`).join(' ')
  }
  const SelectComponent = isCreatable ? CreatableSelect : Select;
  const Placeholder = props => <components.Placeholder {...props} />
  return (
    <>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ onChange, ...props }) => (
          <SelectComponent
            classNamePrefix='select'
            className={valueName}
            isMulti={isMulti}
            components={{ ...cmpnts,Placeholder }}
            placeholder={placeholder}
            isLoading={isLoading}
            options={options}
            noOptionsMessage={() => 'Нет результатов'}
            loadingMessage={() => 'Загрузка...'}
            onInputChange={handleOptions}
            maxMenuHeight={maxMenuHeight}
            isSearchable={isSearchable}
            onChange={(i, a) => {
              onChange(i)
              if (handleOnChange && (a.action === 'select-option' || a.action === 'remove-value' || a.action === 'clear')) {
                handleOnChange(i)
              }
              handleChangeContent && handleChangeContent()
            }}
            filterOption={isSearchable ? undefined : null}
            ref={register ? register() : register}
            {...props}
            {...rest}
          />
        )}
      />
      {err && <FormError msg={message} />}
    </>
  )
}

const viewChildren = function (viewChilds, unregister, register, errors, watch, getValues,setValue, control, reset) {
  return React.Children.map(viewChilds, child => {
    if (child && child.type && child.props && (child.props.h === 'true' || child.props.h === true)) {
      return React.createElement(child.type, {
        ...{
          ...child.props,
          register: register,
          errors: errors,
          watch: watch,
          unregister: unregister,
          getValues: getValues,
          setValue: setValue,
          control: control,
          reset: reset,
          key: child.props.name
        }
      })
    } else {
      if (child && child.props && child.props.children && (child.props.children.length > 0 || typeof child.props.children === 'object')) {
        const newChildren = viewChildren(child.props.children, unregister, register, errors, watch, getValues,setValue, control, reset)
        const type = child.type
        return React.cloneElement(
          {
            ...child, type
          },
          {
            ...child.props,
            children: newChildren
          }
        )
      } else {
        return child
      }
    }
  })
}

export function passParamsThrough(element, props) {
  return viewChildren(
    element,
    props?.unregister,
    props?.register,
    props?.errors,
    props?.watch,
    props?.getValues,
    props?.setValue,
    props?.control,
    props?.reset
  )
}

export function HookForm({ defaultValues, children, onSubmit, validationSchema, predef, mode = 'onBlur', submitOnEnterPress = false }) {
  const methods = useForm({
    mode,
    defaultValues: defaultValues,
    resolver: joiResolver(validationSchema)
  })
  if (predef) {
    predef(methods)
  }
  
  const handleKeyPress = e => {
    if ( submitOnEnterPress === true && e.key === "Enter" && !e.shiftKey ) {
      e.preventDefault();
      methods.handleSubmit(onSubmit)();
    }
  };

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} onKeyPress={handleKeyPress}>
      {passParamsThrough(children, methods)}
    </form>
  )
}
